import { motion } from 'framer-motion';
import Footer from '../components/Footer';

const CommercialFarmingPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div>
      <section className="pt-32 pb-20 bg-gradient-to-br from-green-600 to-green-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">COMMERCIAL FARMING</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
              Large-scale agricultural production and farm management
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={itemVariants} className="relative order-2 md:order-1">
              <img 
                src="/images/commercial_farming.png" 
                alt="Commercial Farming Operations" 
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-900/60 to-transparent rounded-2xl"></div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="order-1 md:order-2 bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-green-600 text-white p-4 rounded-full mr-4">
                  <i className="fas fa-globe-africa text-2xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">OUR MOZAMBIQUE AND ZAMBIA AGRONOMY ACTIVITIES</h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                TEV is currently expanding our agronomy activities to Mozambique and Zambia. This is to address the following needs within the Group:
              </p>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-xl shadow-lg border border-green-200">
                  <div className="flex items-start">
                    <i className="fas fa-seedling text-green-500 text-xl mr-3 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900">Growing Demand</h4>
                      <p className="text-gray-600">Growing demand for certified seed in the region</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-lg border border-blue-200">
                  <div className="flex items-start">
                    <i className="fas fa-chart-line text-blue-500 text-xl mr-3 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900">Production Scaling</h4>
                      <p className="text-gray-600">Diversifying and scaling up primary production to meet growing demand for our value-added products in domestic and export markets</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TEV AFRICA FOODS Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full shadow-xl mb-8">
              <i className="fas fa-building text-white text-2xl mr-3"></i>
              <h2 className="text-white font-bold text-2xl">TEV AFRICA FOODS</h2>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Outgrower Program Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">OUR OUTGROWER PROGRAM</h2>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-8 items-start"
          >
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl">
              <div className="flex items-center mb-8">
                <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-5 rounded-full mr-4 shadow-lg">
                  <i className="fas fa-hands-helping text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Support & Services</h3>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg mb-6 border-l-4 border-green-500">
                <p className="text-gray-700 leading-relaxed text-lg">
                  Our agronomy services are designed to support our outgrowers with knowledge, tools, and field-based guidance they need 
                  to improve crop performance and build sustainable production systems. We combine scientific expertise with practical, on-the
                  ground support to help them make informed decisions that enhance productivity, reduce risks, and protect natural resources.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-r from-green-100 to-green-200 p-6 rounded-xl border border-green-300 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center mb-3">
                    <div className="bg-green-600 text-white p-3 rounded-lg mr-3">
                      <i className="fas fa-flask text-xl"></i>
                    </div>
                    <h4 className="font-bold text-gray-900">Soil Health</h4>
                  </div>
                  <p className="text-gray-700 font-medium">Soil health assessment and fertility analysis</p>
                </div>
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-6 rounded-xl border border-blue-300 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center mb-3">
                    <div className="bg-blue-600 text-white p-3 rounded-lg mr-3">
                      <i className="fas fa-clipboard-list text-xl"></i>
                    </div>
                    <h4 className="font-bold text-gray-900">Crop Management</h4>
                  </div>
                  <p className="text-gray-700 font-medium">Tailored crop management plans</p>
                </div>
                <div className="bg-gradient-to-r from-amber-100 to-amber-200 p-6 rounded-xl border border-amber-300 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center mb-3">
                    <div className="bg-amber-600 text-white p-3 rounded-lg mr-3">
                      <i className="fas fa-bug text-xl"></i>
                    </div>
                    <h4 className="font-bold text-gray-900">Pest Control</h4>
                  </div>
                  <p className="text-gray-700 font-medium">Integrated pest and disease management</p>
                </div>
                <div className="bg-gradient-to-r from-teal-100 to-teal-200 p-6 rounded-xl border border-teal-300 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center mb-3">
                    <div className="bg-teal-600 text-white p-3 rounded-lg mr-3">
                      <i className="fas fa-cloud-sun text-xl"></i>
                    </div>
                    <h4 className="font-bold text-gray-900">Climate Smart</h4>
                  </div>
                  <p className="text-gray-700 font-medium">Climate-smart agricultural practices</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl">
              <div className="flex items-center mb-8">
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-5 rounded-full mr-4 shadow-lg">
                  <i className="fas fa-graduation-cap text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Training & Capacity Building</h3>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg mb-6 border-l-4 border-amber-500">
                <p className="text-gray-700 leading-relaxed text-lg">
                  Training and capacity building are at the heart of our approach. We facilitate 
                  demonstrations, farmer field schools, and hands-on coaching to build local expertise and encourage the adoption of improved practices.
                </p>
              </div>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-amber-100 to-orange-100 p-6 rounded-xl border border-amber-300 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center">
                    <div className="bg-amber-600 text-white p-4 rounded-full mr-4">
                      <i className="fas fa-chalkboard-teacher text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Farmer Field Schools</h4>
                      <p className="text-gray-700">Demonstrations and practical training sessions</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-orange-100 to-red-100 p-6 rounded-xl border border-orange-300 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center">
                    <div className="bg-orange-600 text-white p-4 rounded-full mr-4">
                      <i className="fas fa-user-tie text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Hands-on Coaching</h4>
                      <p className="text-gray-700">Personalized mentorship and guidance</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-red-100 to-pink-100 p-6 rounded-xl border border-red-300 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center">
                    <div className="bg-red-600 text-white p-4 rounded-full mr-4">
                      <i className="fas fa-award text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">Local Expertise</h4>
                      <p className="text-gray-700">Building agricultural knowledge base</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Current Schemes Section */}
      <section className="py-16 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Current & Planned Schemes</h2>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-8"
          >
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl shadow-xl">
              <div className="flex items-center mb-8">
                <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-5 rounded-full mr-4 shadow-lg">
                  <i className="fas fa-map-marked-alt text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Current Schemes (2025)</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center mb-4">
                    <div className="bg-green-600 text-white p-4 rounded-full mr-3">
                      <i className="fas fa-map-pin text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-xl">Chitipa District</h4>
                      <div className="flex items-center mt-2">
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">2 Active Schemes</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-gray-700">Currently operating 2 successful outgrower schemes in Chitipa district</p>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-600 text-white p-4 rounded-full mr-3">
                      <i className="fas fa-map-pin text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-xl">Rumphi District</h4>
                      <div className="flex items-center mt-2">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-bold">1 Active Scheme</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-gray-700">Operating 1 outgrower scheme in Rumphi district</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl shadow-xl">
              <div className="flex items-center mb-8">
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-5 rounded-full mr-4 shadow-lg">
                  <i className="fas fa-calendar-plus text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Planned Expansion (2026)</h3>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-amber-500 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-6">
                  <div className="bg-amber-600 text-white p-4 rounded-full mr-3">
                    <i className="fas fa-rocket text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-xl">New Districts Launch</h4>
                    <p className="text-gray-600">Expanding our reach to 3 new districts</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-r from-amber-100 to-orange-100 p-5 rounded-xl border border-amber-300 hover:shadow-lg transition-shadow duration-300">
                    <div className="text-center">
                      <div className="bg-amber-600 text-white p-3 rounded-full w-12 h-12 mx-auto mb-3">
                        <i className="fas fa-map-marked-alt text-lg"></i>
                      </div>
                      <h5 className="font-bold text-gray-900">Dedza</h5>
                      <p className="text-sm text-gray-600">New District</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-orange-100 to-red-100 p-5 rounded-xl border border-orange-300 hover:shadow-lg transition-shadow duration-300">
                    <div className="text-center">
                      <div className="bg-orange-600 text-white p-3 rounded-full w-12 h-12 mx-auto mb-3">
                        <i className="fas fa-map-marked-alt text-lg"></i>
                      </div>
                      <h5 className="font-bold text-gray-900">Kasungu</h5>
                      <p className="text-sm text-gray-600">New District</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-red-100 to-pink-100 p-5 rounded-xl border border-red-300 hover:shadow-lg transition-shadow duration-300">
                    <div className="text-center">
                      <div className="bg-red-600 text-white p-3 rounded-full w-12 h-12 mx-auto mb-3">
                        <i className="fas fa-map-marked-alt text-lg"></i>
                      </div>
                      <h5 className="font-bold text-gray-900">Mangochi</h5>
                      <p className="text-sm text-gray-600">New District</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CommercialFarmingPage;
